import React, { useState, useEffect } from 'react';
import { API, graphqlOperation } from 'aws-amplify';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, Box, Typography, TableSortLabel, Checkbox, CircularProgress, TextField } from '@mui/material';
import './ShipmentConfirmation.css';

const testTableQuery = `
  query TestTable {
    testTable
  }
`;

const confirmShipmentQuery = `
  query ConfirmShipment($updateShipping: AWSJSON) {
    confirmShipment(updateShipping: $updateShipping)
  }
`;

const markShipmentConfirmedMutation = `
  mutation MarkShipmentConfirmed($amazonOrderId: String!, $purchaseDate: String!) {
    markShipmentConfirmed(amazonOrderId: $amazonOrderId, purchaseDate: $purchaseDate)
  }
`;

interface ShipmentData {
  AmazonOrderId?: string;
  PurchaseDate?: string;
  delivery_method?: string;
  tracking_number?: string;
  tracking_carrier?: string;
  COGS?: string;
  TotalEarnings?: string;
  shipment_confirmed?: boolean;
  tracking_confirmation_day?: number;
  tracking_confirmation_month?: number;
  tracking_confirmation_year?: number;
  order_placed_day?: number;
  order_placed_month?: number;
  order_placed_year?: number;
}

const calcGrossMargin = (shipment: ShipmentData): number | null => {
  const earnings = parseFloat(shipment.TotalEarnings?.replace('$', '') || '');
  const cogs = parseFloat(shipment.COGS?.replace('$', '') || '');
  if (isNaN(earnings) || isNaN(cogs) || earnings === 0) return null;
  return ((earnings - cogs) / earnings) * 100;
};

const CARRIER_MAP: Record<string, string> = {
  'ups': 'UPS',
  'united parcel service': 'UPS',
  'fedex': 'FedEx',
  'federal express': 'FedEx',
  'usps': 'USPS',
  'united states postal service': 'USPS',
  'dhl': 'DHL',
  'ontrac': 'OnTrac',
  'lasership': 'LaserShip',
  'spee-dee': 'SpeeDee',
};

function mapCarrierCode(carrier: string): string {
  if (!carrier) return 'Other';
  const normalized = carrier.trim().toLowerCase();
  return CARRIER_MAP[normalized] || carrier;
}

const ShipmentConfirmation: React.FC = () => {
  const [shipments, setShipments] = useState<ShipmentData[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCopying, setIsCopying] = useState<string | null>(null);
  const [confirmingOrders, setConfirmingOrders] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [orderBy, setOrderBy] = useState<'asc' | 'desc'>('desc');
  const [sortColumn, setSortColumn] = useState<'orderPlaced' | 'trackingConfirmation'>('orderPlaced');

  useEffect(() => {
    fetchShipments();
  }, []);

  const fetchShipments = async () => {
    setLoading(true);
    try {
      const response: any = await API.graphql(graphqlOperation(testTableQuery));
      
      if (!response.data?.testTable) {
        console.error('No testTable data in response');
        return;
      }
      
      const rawData = response.data.testTable;
      
      let data;
      if (typeof rawData === 'string') {
        try {
          data = JSON.parse(rawData);
        } catch (parseError) {
          console.error('Error parsing testTable response:', parseError);
          return;
        }
      } else {
        data = rawData;
      }
      
      let items = [];
      if (data.items) {
        items = data.items;
      }
      
      if (items.length > 0) {
        setShipments(items);
      }
    } catch (error) {
      console.error('Error fetching shipments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmShipment = async (shipment: ShipmentData) => {
    const orderId = shipment.AmazonOrderId;
    if (!orderId || !shipment.tracking_number || !shipment.tracking_carrier) return;

    setConfirmingOrders(prev => new Set(prev).add(orderId));

    try {
      const carrierCode = mapCarrierCode(shipment.tracking_carrier);
      // Split multiple tracking numbers (comma or comma+space separated)
      const trackingNumbers = shipment.tracking_number
        .split(/,\s*/)
        .map(t => t.trim())
        .filter(t => t.length > 0);

      // Submit a separate confirmShipment call for each tracking number
      for (let i = 0; i < trackingNumbers.length; i++) {
        const updateShipping = JSON.stringify({
          order_id: orderId,
          package_detail: {
            packageReferenceId: String(i + 1),
            carrierCode: carrierCode,
            carrierName: shipment.tracking_carrier,
            shippingMethod: 'Standard',
            trackingNumber: trackingNumbers[i],
            shipDate: new Date().toISOString(),
            orderItems: [{ orderItemId: orderId, quantity: 1 }],
          },
        });

        const confirmResponse: any = await API.graphql(
          graphqlOperation(confirmShipmentQuery, { updateShipping })
        );
        console.log(`Confirm shipment response (tracking ${i + 1}/${trackingNumbers.length}):`, confirmResponse);

        // Parse the response to check status code
        const rawResult = confirmResponse?.data?.confirmShipment;
        let statusCode: number | null = null;
        if (rawResult) {
          try {
            const parsed = typeof rawResult === 'string' ? JSON.parse(rawResult) : rawResult;
            statusCode = parsed?.statusCode ?? null;
          } catch {
            console.warn('Could not parse confirmShipment response:', rawResult);
          }
        }

        if (statusCode !== null && statusCode !== 204 && statusCode !== 200) {
          throw new Error(`SP-API returned status ${statusCode} for tracking #${i + 1} (${trackingNumbers[i]}): ${rawResult}`);
        }
      }

      // Only mark as confirmed in TEST_TABLE after ALL tracking numbers succeed
      if (shipment.PurchaseDate) {
        await API.graphql(
          graphqlOperation(markShipmentConfirmedMutation, {
            amazonOrderId: orderId,
            purchaseDate: shipment.PurchaseDate,
          })
        );
      }

      // Update local state
      setShipments(prev =>
        prev.map(s =>
          s.AmazonOrderId === orderId ? { ...s, shipment_confirmed: true } : s
        )
      );
    } catch (error) {
      console.error(`Error confirming shipment for ${orderId}:`, error);
      alert(`Failed to confirm shipment for ${orderId}. Check console for details.`);
    } finally {
      setConfirmingOrders(prev => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    }
  };

  const formatTrackingDate = (shipment: ShipmentData): string => {
    if (shipment.tracking_confirmation_day && shipment.tracking_confirmation_month && shipment.tracking_confirmation_year) {
      return `${shipment.tracking_confirmation_year}/${String(shipment.tracking_confirmation_month).padStart(2, '0')}/${String(shipment.tracking_confirmation_day).padStart(2, '0')}`;
    }
    return 'N/A';
  };

  const formatOrderPlacedDate = (shipment: ShipmentData): string => {
    if (shipment.order_placed_day && shipment.order_placed_month && shipment.order_placed_year) {
      return `${shipment.order_placed_year}/${String(shipment.order_placed_month).padStart(2, '0')}/${String(shipment.order_placed_day).padStart(2, '0')}`;
    }
    return 'N/A';
  };

  const getTrackingDateForSort = (shipment: ShipmentData): Date => {
    if (shipment.tracking_confirmation_day && shipment.tracking_confirmation_month && shipment.tracking_confirmation_year) {
      return new Date(shipment.tracking_confirmation_year, shipment.tracking_confirmation_month - 1, shipment.tracking_confirmation_day);
    }
    return new Date(0);
  };

  const getOrderPlacedDateForSort = (shipment: ShipmentData): Date => {
    if (shipment.order_placed_day && shipment.order_placed_month && shipment.order_placed_year) {
      return new Date(shipment.order_placed_year, shipment.order_placed_month - 1, shipment.order_placed_day);
    }
    return new Date(0);
  };

  const handleSort = (column: 'orderPlaced' | 'trackingConfirmation') => {
    if (sortColumn === column) {
      setOrderBy(orderBy === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setOrderBy('desc');
    }
  };

  const filteredShipments = shipments.filter(s => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.trim().toLowerCase();
    return (
      (s.AmazonOrderId || '').toLowerCase().includes(q) ||
      (s.tracking_number || '').toLowerCase().includes(q) ||
      (s.tracking_carrier || '').toLowerCase().includes(q)
    );
  });

  const sortedShipments = [...filteredShipments].sort((a, b) => {
    const getDate = sortColumn === 'orderPlaced' ? getOrderPlacedDateForSort : getTrackingDateForSort;
    const dateA = getDate(a);
    const dateB = getDate(b);
    
    if (orderBy === 'asc') {
      return dateA.getTime() - dateB.getTime();
    } else {
      return dateB.getTime() - dateA.getTime();
    }
  });

  const handleCopy = async (text: string, id: string) => {
    setIsCopying(id);
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
    setTimeout(() => setIsCopying(null), 1000);
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedShipments.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedShipments.length / itemsPerPage);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  if (loading) {
    return <div className="shipment-confirmation-container">Loading...</div>;
  }

  return (
    <div className="shipment-confirmation-container">
      <h1>Shipment Confirmations</h1>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Total items: {shipments.length}
      </Typography>

      <TextField
        label="Search by Order ID, Tracking #, or Carrier"
        variant="outlined"
        size="small"
        fullWidth
        value={searchQuery}
        onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
        sx={{ mb: 2, maxWidth: 500 }}
      />

      {filteredShipments.length !== shipments.length && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          Showing {filteredShipments.length} of {shipments.length} items
        </Typography>
      )}
      
      {currentItems.length > 0 ? (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">Confirmed</TableCell>
                  <TableCell>Order ID</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortColumn === 'orderPlaced'}
                      direction={sortColumn === 'orderPlaced' ? orderBy : 'desc'}
                      onClick={() => handleSort('orderPlaced')}
                    >
                      Order Placed
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>COGS</TableCell>
                  <TableCell>Delivery Method</TableCell>
                  <TableCell>Tracking Number</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortColumn === 'trackingConfirmation'}
                      direction={sortColumn === 'trackingConfirmation' ? orderBy : 'desc'}
                      onClick={() => handleSort('trackingConfirmation')}
                    >
                      Tracking Confirmation
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Gross Margin %</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentItems.map((shipment, index) => {
                  const margin = calcGrossMargin(shipment);
                  const rowColor = margin === null ? undefined : margin < 0 ? '#ffcccc' : '#cce5ff';
                  const isConfirmed = shipment.shipment_confirmed === true;
                  const isConfirming = confirmingOrders.has(shipment.AmazonOrderId || '');
                  const canConfirm = !!(shipment.tracking_number && shipment.tracking_carrier && shipment.AmazonOrderId);

                  return (
                  <TableRow key={index} sx={{ backgroundColor: rowColor }}>
                    <TableCell padding="checkbox">
                      {isConfirming ? (
                        <CircularProgress size={20} />
                      ) : (
                        <Checkbox
                          checked={isConfirmed}
                          disabled={isConfirmed || !canConfirm}
                          onChange={() => handleConfirmShipment(shipment)}
                          title={
                            isConfirmed
                              ? 'Shipment already confirmed'
                              : !canConfirm
                              ? 'Missing tracking info'
                              : 'Click to confirm shipment with Amazon'
                          }
                        />
                      )}
                    </TableCell>
                    <TableCell 
                      onClick={() => handleCopy(shipment.AmazonOrderId || '', `order-${index}`)}
                      className="copyable-cell"
                    >
                      {shipment.AmazonOrderId || 'N/A'}
                      {isCopying === `order-${index}` && <span className="copy-indicator">✓</span>}
                    </TableCell>
                    <TableCell 
                      onClick={() => handleCopy(formatOrderPlacedDate(shipment), `orderplaced-${index}`)}
                      className="copyable-cell"
                    >
                      {formatOrderPlacedDate(shipment)}
                      {isCopying === `orderplaced-${index}` && <span className="copy-indicator">✓</span>}
                    </TableCell>
                    <TableCell 
                      onClick={() => handleCopy(shipment.COGS || '', `cogs-${index}`)}
                      className="copyable-cell"
                    >
                      {shipment.COGS ? `${shipment.COGS}` : 'N/A'}
                      {isCopying === `cogs-${index}` && <span className="copy-indicator">✓</span>}
                    </TableCell>
                    <TableCell 
                      onClick={() => handleCopy(shipment.tracking_carrier || '', `delivery-${index}`)}
                      className="copyable-cell"
                    >
                      {shipment.tracking_carrier || 'N/A'}
                      {isCopying === `delivery-${index}` && <span className="copy-indicator">✓</span>}
                    </TableCell>
                    <TableCell className="copyable-cell">
                      {shipment.tracking_number ? (
                        <div>
                          {shipment.tracking_number.split(', ').slice(0, 5).map((trackingNum, trackingIndex) => (
                            <div 
                              key={trackingIndex}
                              onClick={() => handleCopy(trackingNum.trim(), `tracking-${index}-${trackingIndex}`)}
                              style={{ cursor: 'pointer', marginBottom: '4px' }}
                            >
                              {trackingNum.trim()}
                              {isCopying === `tracking-${index}-${trackingIndex}` && <span className="copy-indicator">✓</span>}
                            </div>
                          ))}
                        </div>
                      ) : (
                        'N/A'
                      )}
                    </TableCell>
                    <TableCell 
                      onClick={() => handleCopy(formatTrackingDate(shipment), `date-${index}`)}
                      className="copyable-cell"
                    >
                      {formatTrackingDate(shipment)}
                      {isCopying === `date-${index}` && <span className="copy-indicator">✓</span>}
                    </TableCell>
                    <TableCell>
                      {margin !== null ? `${margin.toFixed(1)}%` : 'N/A'}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination */}
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2, gap: 2 }}>
            <Button 
              variant="outlined"
              onClick={() => paginate(currentPage - 1)} 
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            
            <Typography>Page {currentPage} of {totalPages}</Typography>
            
            <Button 
              variant="outlined"
              onClick={() => paginate(currentPage + 1)} 
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </Box>
        </>
      ) : (
        <Typography>No shipment data available.</Typography>
      )}
    </div>
  );
};

export default ShipmentConfirmation;
